function fadeInImages(root = document) {
  if (!root?.querySelectorAll) root = document;
  const lazyImageContainers = Array.from(root.querySelectorAll('.fade-in-image'));
  const lazyImages = Array.from(root.querySelectorAll('.fade-in-image img'));
  if (root.matches?.('.fade-in-image')) lazyImageContainers.unshift(root);
  if (root.matches?.('.fade-in-image img')) {
    lazyImages.unshift(root);
    lazyImageContainers.push(root.closest('.fade-in-image'));
  }

  if ("IntersectionObserver" in window) {
    if (!lazyImages.length && !fadeInImages.targets?.size) return;
    fadeInImages.targets ??= new Set();
    fadeInImages.observer ??= new IntersectionObserver(function(entries, observer) {
      entries.forEach(function(entry) {
        if (!fadeInImages.targets.has(entry.target)) return;
        const parent = entry.target.closest('.fade-in-image');
        if (!entry.target.isConnected || !parent) {
          observer.unobserve(entry.target);
          fadeInImages.targets.delete(entry.target);
          return;
        }
        if (entry.isIntersecting) {
          parent.classList.add("fade-in-image--loaded");
        } else {
          parent.classList.remove("fade-in-image--loaded");
        }
      });
    });

    // 필터의 innerHTML 갱신에서 분리된 이미지도 재등록 전에 해제한다.
    fadeInImages.targets.forEach(image => {
      if (!image.isConnected || !image.closest('.fade-in-image')) {
        fadeInImages.observer.unobserve(image);
        fadeInImages.targets.delete(image);
      }
    });
    lazyImages.forEach(function(lazyImage) {
      fadeInImages.targets.add(lazyImage);
      fadeInImages.observer.observe(lazyImage);
    });
  } else {
    // Fallback if IntersectionObserver is not supported
    lazyImageContainers.forEach(function(container) {
      container.classList.add("fade-in-image--loaded");
    });
  }
}

function unloadFadeInImages(root) {
  fadeInImages.targets?.forEach(image => {
    if (image === root || root.contains(image)) {
      fadeInImages.observer.unobserve(image);
      fadeInImages.targets.delete(image);
    }
  });
}

function watchForResize(excludedRoot = null) {
  const siteHeaderElement = document.getElementById('site-header');
  const siteFooterElement = document.getElementById('site-footer');
  watchForResize.observer ??= new ResizeObserver(entries => {
    if (entries.some(entry => entry.target.isConnected && ['site-header', 'site-footer'].includes(entry.target.id))) setCssVariables();
  });
  // 편집기의 헤더·푸터 교체에서도 이전 요소 관측을 먼저 해제한다.
  watchForResize.observer.disconnect();
  [siteHeaderElement, siteFooterElement].forEach(element => {
    if (element && !excludedRoot?.contains?.(element)) watchForResize.observer.observe(element);
  });
}

function loadAnimations(root = document) {
  if (!root?.querySelectorAll) root = document;
  const animationContainers = Array.from(root.querySelectorAll(".animate"));
  if (root.matches?.('.animate')) animationContainers.unshift(root);

  if ("IntersectionObserver" in window) {
    loadAnimations.observer ??= new IntersectionObserver(function(entries, observer) {
      entries.forEach(function(entry) {
        if (!entry.target.isConnected) {
          observer.unobserve(entry.target);
          return;
        }
        if (entry.isIntersecting) {
          entry.target.classList.add("animate--loaded");
        } else {
          entry.target.classList.remove("animate--loaded");
        }
      });
    });

    animationContainers.forEach(function(container) {
      loadAnimations.observer.observe(container);
    });
  } else {
    // Fallback if IntersectionObserver is not supported
    animationContainers.forEach(function(container) {
      container.classList.add("animate--loaded");
    });
  }
}

function unloadAnimations(root) {
  if (!loadAnimations.observer) return;
  if (root.matches?.('.animate')) loadAnimations.observer.unobserve(root);
  root.querySelectorAll('.animate').forEach(element => loadAnimations.observer.unobserve(element));
}

function setCssVariables(excludedRoot = null) {
  const root = document.documentElement;
  const siteHeaderElement = document.getElementById('site-header');
  const siteFooterElement = document.getElementById('site-footer');

  if (siteHeaderElement && !excludedRoot?.contains?.(siteHeaderElement)) {
    const headerHeight = siteHeaderElement.offsetHeight;
    root.style.setProperty('--site-header-height', headerHeight + "px");
    siteHeaderElement.querySelectorAll('animated-details').forEach(element => element.positionHeaderDropdown?.());
  } else {
    root.style.removeProperty('--site-header-height');
  }

  if (siteFooterElement && !excludedRoot?.contains?.(siteFooterElement)) {
    const footerHeight = siteFooterElement.offsetHeight;
    root.style.setProperty('--site-footer-height', footerHeight + "px");
  } else {
    root.style.removeProperty('--site-footer-height');
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

const inactiveHTMLRoots = new WeakSet();

function isCurrentHTMLNode(node) {
  if (!node.isConnected) return false;
  for (let current = node; current; current = current.parentElement) {
    if (inactiveHTMLRoots.has(current)) return false;
  }
  return true;
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      const formId = element.getAttribute('form');
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      formId && element.setAttribute('form', `${formId}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';
    inactiveHTMLRoots.add(oldNode);
    unloadAnimations(oldNode);
    unloadFadeInImages(oldNode);
    loadAnimations(newNode);
    fadeInImages(newNode);
    initializeDetails(newNode);
    initSwipers(newNode);
    initMediaGallery(newNode);
    disposeProductLightboxes(oldNode);
    initProductLightboxes(newNode);

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      // 상품 확대보기는 범위별 초기화가 라이브러리를 한 번만 불러온다.
      if (oldScriptTag.src?.split(/[?#]/)[0].endsWith('/vendor-fslightbox.js')) return;
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

const initializedDetails = new WeakSet();

function syncDetailsSummary(details) {
  const summary = details.querySelector(':scope > summary');
  if (!summary) return;
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', String(details.open));
  const panelId = summary.nextElementSibling?.id;
  if (panelId) summary.setAttribute('aria-controls', panelId);
  else summary.removeAttribute('aria-controls');
}

function initializeDetails(root = document) {
  root.querySelectorAll('[id^="Details-"] summary').forEach(summary => {
    const details = summary.parentElement;
    if (!details.matches('details')) return;
    syncDetailsSummary(details);
    if (initializedDetails.has(details)) return;
    details.addEventListener('toggle', () => syncDetailsSummary(details));
    details.addEventListener('keyup', onKeyUpEscape);
    initializedDetails.add(details);
  });
}

initializeDetails();

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container, ownership = null) {
  removeTrapFocus();

  trapFocusHandlers.keydown = function(event) {
    if (event.defaultPrevented || isComposingEvent(event)) return;
    if (event.key !== 'Tab' && event.code?.toUpperCase() !== 'TAB') return; // If not TAB key
    const elements = getFocusableElements(container).filter(element =>
      element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[inert]') &&
      element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
    );
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (!first) {
      event.preventDefault();
      container.focus();
      return;
    }
    const direction = event.shiftKey ? Node.DOCUMENT_POSITION_PRECEDING : Node.DOCUMENT_POSITION_FOLLOWING;
    const hasNextTarget = container.contains(event.target) && elements.some(element =>
      Boolean(event.target.compareDocumentPosition(element) & direction)
    );
    if (!hasNextTarget) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  };

  trapFocusHandlers.container = container;
  document.addEventListener('keydown', trapFocusHandlers.keydown);
  // focus() dispatches synchronously and may install another owner's trap.
  // Publish the handler we created before dispatch, never capture it afterwards.
  if (ownership) ownership.focusTrapHandler = trapFocusHandlers.keydown;
  elementToFocus.focus();
}

function pauseAllMedia() {
  document.querySelectorAll('slideshow-media, youtube-background, kotsam-editorial-gallery').forEach((media) => {
    if (typeof media.pauseBackground === 'function') media.pauseBackground();
    else media.setAttribute('data-external-paused', '');
  });
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('.js-hosted-video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  trapFocusHandlers.container = null;

  if (elementToFocus) elementToFocus.focus();
}

function isComposingEvent(event) {
  return event.isComposing || event.keyCode === 229 ||
    Boolean(event.target?.closest?.('search-form, predictive-search')?.isComposing);
}

function onKeyUpEscape(event) {
  if (event.defaultPrevented || isComposingEvent(event)) return;
  if (event.key !== 'Escape' && event.code?.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  if (summaryElement.hasAttribute('aria-expanded')) {
    summaryElement.setAttribute('aria-expanded', 'false');
  }
  summaryElement.focus();
  event.stopPropagation();
}

const serializeForm = form => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.changeEvent = new Event('change', { bubbles: true })
    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (this.input.dataset.min === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".js-quantity-selector-button-minus");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".js-quantity-selector-button-plus");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.textContent) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.textContent = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.textContent = values[i];
      selector.appendChild(opt);
    }
  }
};

function syncDrawerOverlayState() {
  document.documentElement.classList.toggle(
    'overlay-on',
    Boolean(document.querySelector('[data-drawer-dialog].menu-opening, [data-drawer-dialog].open-in-editor'))
  );
}

class DrawerOpener extends HTMLElement {
  static drawerOwners = new WeakMap();

  constructor() {
    super();

    this.drawerId = this.dataset.drawerId;
    this.drawerOpenButtons = document.querySelectorAll(`[data-drawer-to-open="${this.drawerId}"]`);
    this.onOpenButtonClick = this.onOpenButtonClick.bind(this);
    this.onCloseButtonClick = this.onCloseButtonClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onBodyClickEvent = this.onBodyClick.bind(this);
    this.onSectionUnload = this.onSectionUnload.bind(this);
    this.onMenuBreakpointChange = this.onMenuBreakpointChange.bind(this);
  }

  connectedCallback() {
    this.drawerOpenButtons.forEach(button => button.removeEventListener('click', this.onOpenButtonClick));
    this.drawerId = this.dataset.drawerId;
    this.drawerOpenButtons = document.querySelectorAll(`[data-drawer-to-open="${this.drawerId}"]`);
    document.addEventListener('shopify:section:unload', this.onSectionUnload);
    this.drawerOpenButtons.forEach(button => button.addEventListener('click', this.onOpenButtonClick));
    if (this.targetDrawer && DrawerOpener.drawerOwners.get(this.targetDrawer) === this.drawerOwnership) {
      const expanded = this.targetDrawer.classList.contains('menu-opening');
      this.drawerOpenButtons.forEach(button => button.setAttribute('aria-expanded', String(expanded)));
      if (!this.returnFocus?.isConnected) this.returnFocus = this.drawerOpenButtons[0] || null;
    }
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:unload', this.onSectionUnload);
    this.drawerOpenButtons.forEach(button => button.removeEventListener('click', this.onOpenButtonClick));
    if (!this.transferCartDrawerOwnership()) this.closeDrawer(false, true);
  }

  transferCartDrawerOwnership() {
    // 카운터 section 교체 시 살아 있는 drawer의 제어권을 새 헤더에 연결한다.
    const replacement = this.id === 'cart-drawer-opener' && document.getElementById(this.id);
    const drawer = this.targetDrawer;
    if (!(replacement instanceof DrawerOpener) || replacement === this ||
        replacement.drawerId !== this.drawerId || !drawer?.isConnected ||
        !drawer.classList.contains('menu-opening') ||
        DrawerOpener.drawerOwners.get(drawer) !== this.drawerOwnership) return false;

    document.body.removeEventListener('click', this.onBodyClickEvent);
    drawer.removeEventListener('keyup', this.onKeyUp);
    (this.drawerCloseButtons || []).forEach(button => button.removeEventListener('click', this.onCloseButtonClick));
    for (const key of ['targetDrawer', 'drawerSection', 'drawerCloseButtons', 'drawerOwnership', 'ownedTrap']) {
      replacement[key] = this[key];
      this[key] = null;
    }
    replacement.returnFocus = this.returnFocus?.isConnected ? this.returnFocus : replacement.drawerOpenButtons[0];
    this.returnFocus = null;
    replacement.closingAnimation = null;
    document.body.addEventListener('click', replacement.onBodyClickEvent);
    drawer.addEventListener('keyup', replacement.onKeyUp);
    (replacement.drawerCloseButtons || []).forEach(button => button.addEventListener('click', replacement.onCloseButtonClick));
    replacement.drawerOpenButtons.forEach(button => button.setAttribute('aria-expanded', true));
    return true;
  }

  onSectionUnload(event) {
    if (!this.targetDrawer || event.target !== this.drawerSection) return;
    if (DrawerOpener.drawerOwners.get(this.targetDrawer) === this.drawerOwnership) {
      this.targetDrawer.classList.remove('open-in-editor');
    }
    this.closeDrawer(false, true);
  }

  finishClose(ownership) {
    if (this.drawerOwnership !== ownership) return;
    const targetDrawer = this.targetDrawer;
    if (DrawerOpener.drawerOwners.get(targetDrawer) === ownership) {
      targetDrawer.hidden = true;
      targetDrawer.removeAttribute('open');
      DrawerOpener.drawerOwners.delete(targetDrawer);
    }
    this.closingAnimation = null;
    this.drawerOwnership = null;
    this.targetDrawer = null;
    this.drawerSection = null;
    this.drawerCloseButtons = null;
    this.returnFocus = null;
  }

  onKeyUp(event) {
    if (event.defaultPrevented || isComposingEvent(event)) return;
    if(event.key !== 'Escape' && event.code?.toUpperCase() !== 'ESCAPE') return;
    const animatedDetails = event.target.closest('animated-details.is-open');
    if (animatedDetails) {
      animatedDetails.close(true);
    } else if (event.target.closest('details[open]')) {
      onKeyUpEscape(event);
    } else {
      this.closeDrawer();
    }
    event.stopPropagation();
  }

  onOpenButtonClick(event) {
    const eventButton = event.currentTarget;
    event.preventDefault();
    this.openDrawer(eventButton);
  }

  onCloseButtonClick(event) {
    event.preventDefault();
    this.closeDrawer();
  }

  openDrawer(opener = document.activeElement) {
    const targetDrawer = document.getElementById(this.drawerId);
    if (!targetDrawer && this.drawerId === 'menu-drawer') {
      console.error(`The mobile menu is not found in the DOM. Make sure that the section within the "Header Group" is enabled in the theme editor.`);
    } else if (!targetDrawer) {
      console.error(`${this.drawerId} is not found in the DOM. Make sure that the section is enabled in the theme editor.`);
    }
    if (!targetDrawer) return;
    if (this.targetDrawer && this.targetDrawer !== targetDrawer) this.closeDrawer(false, true);
    if (targetDrawer.classList.contains('menu-opening')) return;

    const drawerCloseButtons = targetDrawer.querySelectorAll(`[data-drawer-to-close="${this.drawerId}"]`);
    this.targetDrawer = targetDrawer;
    this.drawerSection = targetDrawer.closest('[id^="shopify-section-"]');
    this.drawerCloseButtons = drawerCloseButtons;
    const ownership = {};
    this.drawerOwnership = ownership;
    this.ownedTrap = null;
    DrawerOpener.drawerOwners.set(targetDrawer, ownership);
    this.returnFocus = opener instanceof HTMLElement ? opener : document.activeElement;
    this.closingAnimation = null;
    document.body.addEventListener('click', this.onBodyClickEvent);
    targetDrawer.addEventListener('keyup', this.onKeyUp);
    targetDrawer.removeAttribute('hidden');
    targetDrawer.setAttribute('open', true);
    targetDrawer.getBoundingClientRect();
    targetDrawer.classList.add('menu-opening');
    document.documentElement.classList.add('overlay-on');
    this.drawerOpenButtons.forEach(button => {
      button.setAttribute('aria-expanded', true);
    });
    drawerCloseButtons.forEach(button => button.addEventListener('click', this.onCloseButtonClick));
    trapFocus(targetDrawer, drawerCloseButtons[0] || targetDrawer, ownership);
    if (this.drawerOwnership !== ownership || DrawerOpener.drawerOwners.get(targetDrawer) !== ownership) return;
    if (!this.isConnected || !targetDrawer.isConnected) {
      this.closeDrawer(false, true);
      return;
    }
    this.ownedTrap = ownership.focusTrapHandler;
    if (targetDrawer.classList.contains('drawer--menu')) {
      this.menuBreakpoint ??= window.matchMedia('(min-width: 900px)');
      this.menuBreakpoint.addEventListener('change', this.onMenuBreakpointChange);
      this.onMenuBreakpointChange(this.menuBreakpoint);
    }
  }

  onMenuBreakpointChange({ matches }) {
    const drawer = this.targetDrawer;
    if (!matches || !drawer?.classList.contains('drawer--menu')) return;
    if (DrawerOpener.drawerOwners.get(drawer) !== this.drawerOwnership) return;
    const ownsTrap = this.ownedTrap && trapFocusHandlers.keydown === this.ownedTrap && trapFocusHandlers.container === drawer;
    const hadFocus = drawer.contains(document.activeElement) || (ownsTrap && document.activeElement === document.body);
    this.closeDrawer(false, true);
    if (hadFocus && !trapFocusHandlers.container) {
      const logo = this.closest('.site-header')?.querySelector('.site-header__heading a');
      if (logo?.getClientRects().length && getComputedStyle(logo).visibility !== 'hidden') logo.focus();
    }
  }

  closeDrawer(focusOpener = true, immediate = false) {
    this.menuBreakpoint?.removeEventListener('change', this.onMenuBreakpointChange);
    const targetDrawer = this.targetDrawer;
    const ownership = this.drawerOwnership;
    if (!targetDrawer || !ownership) return;
    const ownsDrawer = DrawerOpener.drawerOwners.get(targetDrawer) === ownership;
    if (!immediate && ownsDrawer && !targetDrawer.classList.contains('menu-opening')) return;

    document.body.removeEventListener('click', this.onBodyClickEvent);
    targetDrawer.removeEventListener('keyup', this.onKeyUp);
    (this.drawerCloseButtons || []).forEach(button => button.removeEventListener('click', this.onCloseButtonClick));
    const returnFocus = this.returnFocus;
    const handler = ownership.focusTrapHandler;
    const releasesTrap = handler && trapFocusHandlers.keydown === handler && trapFocusHandlers.container === targetDrawer;
    if (releasesTrap) removeTrapFocus();
    this.ownedTrap = null;
    if (ownsDrawer) {
      targetDrawer.classList.remove('menu-opening');
      this.drawerOpenButtons.forEach(button => button.setAttribute('aria-expanded', false));
    }
    syncDrawerOverlayState();

    if (immediate || !ownsDrawer) {
      this.finishClose(ownership);
    } else {
      const closingAnimation = Promise.allSettled(targetDrawer.getAnimations().map(animation => animation.finished));
      this.closingAnimation = closingAnimation;
      closingAnimation.then(() => {
        if (this.closingAnimation !== closingAnimation) return;
        this.finishClose(ownership);
      });
    }
    // 초점 복귀가 새 drawer를 열어도 이전 닫기 작업이 새 소유권을 덮지 않는다.
    if (releasesTrap && focusOpener && returnFocus?.isConnected && !trapFocusHandlers.container) returnFocus.focus();
  }

  onBodyClick(event) {
    if (event.target.classList.contains('page-layout__overlay')) this.closeDrawer();
  }

}

customElements.define('drawer-opener', DrawerOpener);

class SimpleModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this)
    );
    this.addEventListener('keyup', (event) => {
      if (event.defaultPrevented || isComposingEvent(event)) return;
      if (!this.hasAttribute('open') || (trapFocusHandlers.container && trapFocusHandlers.container !== this)) return;
      if (event.key === 'Escape' || event.code?.toUpperCase() === 'ESCAPE') {
        event.stopPropagation();
        this.hide();
      }
    });
  }

  show(opener) {
    this.openedBy = opener;
    this.setAttribute('open', '');
    this.removeAttribute('hidden');
    trapFocus(this, this.querySelector('[id^="ModalClose-"]'), this);
  }

  disconnectedCallback() {
    this.hide();
  }

  hide() {
    if (!this.hasAttribute('open')) return;
    const opener = this.openedBy;
    const handler = this.focusTrapHandler;
    this.focusTrapHandler = null;
    this.openedBy = null;
    this.removeAttribute('open');
    this.setAttribute('hidden', '');
    if (handler && trapFocusHandlers.keydown === handler && trapFocusHandlers.container === this) {
      removeTrapFocus(opener?.isConnected ? opener : null);
    }
  }
}
customElements.define('simple-modal-dialog', SimpleModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector(`[id="Deferred-Poster-${this.dataset.mediaId}"]`);
    if (!poster) return;
    poster.addEventListener('click', (event) => this.loadContent());
  }

  disconnectedCallback() {
    this.querySelectorAll('video').forEach(video => video.pause());
    this.modelViewerUI?.pause?.();
    const frames = this.querySelectorAll('iframe');
    frames.forEach(frame => frame.remove());
    if (frames.length) this.removeAttribute('loaded');
  }

  loadContent(focus = true) {
    if (!this.isConnected) return;
    window.pauseAllMedia();
    let deferredElement = this.querySelector('video, model-viewer, iframe');
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
    }
    if (deferredElement?.nodeName === 'VIDEO' && deferredElement.hasAttribute('autoplay')) {
      // 재방문한 자동재생 영상만 재개한다. 재생 거부 시 기본 컨트롤과 실패 상태를 유지한다.
      deferredElement.play().catch(() => {});
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.prevButton || !this.nextButton) return;

    this.onSliderScroll = this.update.bind(this);
    this.onSliderButtonClick = this.onButtonClick.bind(this);
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isConnected) this.initPages();
    });
  }

  connectedCallback() {
    if (!this.resizeObserver) return;

    this.resetPages();
    this.resizeObserver.observe(this.slider);
    this.slider.addEventListener('scroll', this.onSliderScroll);
    this.prevButton.addEventListener('click', this.onSliderButtonClick);
    this.nextButton.addEventListener('click', this.onSliderButtonClick);
  }

  disconnectedCallback() {
    if (!this.resizeObserver) return;

    this.resizeObserver.disconnect();
    this.slider.removeEventListener('scroll', this.onSliderScroll);
    this.prevButton.removeEventListener('click', this.onSliderButtonClick);
    this.nextButton.removeEventListener('click', this.onSliderButtonClick);
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    const count = this.sliderItemsToShow.length;
    this.sliderItemOffset = count > 1 ? this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft : 0;
    if (count < 2 || this.slider.clientWidth <= 0 || !Number.isFinite(this.sliderItemOffset) || this.sliderItemOffset <= 0) {
      this.sliderItemOffset = 0;
      this.step = 0;
      this.slideScrollPosition = 0;
      this.currentPage = count ? 1 : 0;
      this.slidesPerPage = count;
      this.totalPages = count ? 1 : 0;
      this.prevButton?.setAttribute('disabled', 'disabled');
      this.nextButton?.setAttribute('disabled', 'disabled');
      return;
    }
    this.slidesPerPage = Math.max(1, Math.min(count, Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset)));
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.slidesInView();
    this.step = Math.max(1, this.step);
    this.prevButton?.removeAttribute('disabled');
    this.nextButton?.removeAttribute('disabled');
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (this.sliderItemsToShow.length < 2 || this.slider.clientWidth <= 0 || !Number.isFinite(this.sliderItemOffset) || this.sliderItemOffset <= 0) {
      this.initPages();
      return;
    }
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.enableSliderLooping) return;

    const slideSpacingLeft = this.dataset.slideSpacingLeft || 0;

    if (!this.prevButton) return;
    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft <= slideSpacingLeft) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  slidesInView() {
    let slidesInView = 0;

    this.sliderItemsToShow.forEach((element) => {
      if (this.isSlideVisible(element)) {
        return slidesInView++;
      }
    });

    return this.step = slidesInView;
  }

  onButtonClick(event) {
    event.preventDefault();
    if (this.slider.clientWidth <= 0 || !Number.isFinite(this.step) || this.step <= 0 || !Number.isFinite(this.sliderItemOffset) || this.sliderItemOffset <= 0) return;

    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (this.step * this.sliderItemOffset) : this.slider.scrollLeft - (this.step * this.sliderItemOffset);
    if (!Number.isFinite(this.slideScrollPosition)) return;
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }
}

customElements.define('slider-component', SliderComponent);

class VariantSelects extends HTMLElement {
  static selectionGenerations = new WeakMap();

  constructor() {
    super();
    this.productFormId = this.dataset.productFormId;
    this.productId = this.dataset.productId;
    this.onChange = this.onChange.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.scrollGuardTimer = null;
    this.selectionRequestId = 0;
  }

  connectedCallback() {
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.selectionRequestId += 1;
    this.releaseScrollGuard();
  }

  onChange(event) {
    if (event.target.matches('input[type="radio"]')) {
      VariantSelects.scrollGuardOwner?.releaseScrollGuard();
      this.scrollGuardTop = window.scrollY || document.documentElement.scrollTop;
      this.scrollGuardLeft = window.scrollX || document.documentElement.scrollLeft;
      VariantSelects.scrollGuardOwner = this;
      window.addEventListener('scroll', this.onScroll);
      this.scrollGuardTimer = setTimeout(() => this.releaseScrollGuard(), 500);
    }
    const target = this.getInputForEventTarget(event.target);
    const requestId = ++this.selectionRequestId;
    const owner = this.closest('product-info, product-card-info');
    const generation = owner ? (VariantSelects.selectionGenerations.get(owner) || 0) + 1 : 0;
    if (owner) VariantSelects.selectionGenerations.set(owner, generation);
    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: { event, target, selectedOptionValues: this.selectedOptionValues },
    }).catch(error => {
      console.error('Product option-selection subscriber failed', error);
      if (!this.isConnected || requestId !== this.selectionRequestId || !owner ||
          VariantSelects.selectionGenerations.get(owner) !== generation) return;
      const forms = owner.matches('product-info')
        ? owner.productForms || []
        : [owner.quickAddProductForm?.closest('product-form')].filter(Boolean);
      forms.forEach(form => {
        form.querySelector('[type="submit"]')?.setAttribute('disabled', 'disabled');
        if (form.querySelector('[data-error-message-wrapper]')) {
          form.handleErrorMessage?.(window.variantStrings.error, true);
        }
      });
    });
  }

  onScroll() {
    window.scrollTo(this.scrollGuardLeft, this.scrollGuardTop);
  }

  releaseScrollGuard() {
    if (this.scrollGuardTimer !== null) clearTimeout(this.scrollGuardTimer);
    this.scrollGuardTimer = null;
    window.removeEventListener('scroll', this.onScroll);
    if (VariantSelects.scrollGuardOwner === this) VariantSelects.scrollGuardOwner = null;
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    let selectedOptionValueIds = [];

    this.querySelectorAll('fieldset').forEach((selector) => {
      if (selector.querySelector('select')) {
        selectedOptionValueIds.push(selector.querySelector('select').selectedOptions[0].dataset.optionValueId);
      } else {
        selectedOptionValueIds.push(selector.querySelector('input[type="radio"]:checked').dataset.optionValueId);
      }
    });

    return selectedOptionValueIds;
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductCardMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.productCardContainer = this.closest('.product-card');
  }

  setActiveMedia(variantMediaId) {
    const variantMedia = this.querySelector(`[data-media-id="${variantMediaId}"]`);
    let selectedActiveMedia;
    if (variantMedia) {
      selectedActiveMedia = variantMedia;
    } else {
      selectedActiveMedia = this.querySelector(`[data-media-id="featured-image"]`);
    };
    if (!selectedActiveMedia) return;

    this.querySelectorAll('[data-gallery-media-container]').forEach((media) => media.classList.remove('is-active'));

    selectedActiveMedia.classList.add('is-active');
  }
}

customElements.define('product-card-media-gallery', ProductCardMediaGallery);

class AnimatedDetails extends HTMLElement {
  constructor() {
    super();
    this.elementToggle = this.querySelector('[data-animated-details-toggle]');
    this.elementTarget = this.querySelector('[data-animated-details-target]');
    this.elementParent = this.closest('[data-animated-details-parent]');
    this.onKeyupEvent = this.onKeyup.bind(this);

    this.elementToggle.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.classList.contains('is-open')) {
        this.close();
      } else {
        this.open();
      }
    });

    this.elementToggle.addEventListener('keyup', this.onKeyupEvent);
  }

  connectedCallback() {
    this.stateObserver ??= new MutationObserver(() => this.syncTargetState());
    this.stateObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
    this.syncTargetState();
    this.positionHeaderDropdown();
    if (this.classList.contains('is-open')) this.addOpenListeners();
  }

  positionHeaderDropdown() {
    if (!this.elementTarget.matches('.site-header__dropdown-menu') || this.classList.contains('localization__dropdown-link')) return;
    const header = this.closest('.site-header');
    if (!header) return;
    this.elementTarget.style.setProperty('--dropdown-horizontal-offset', '0px');
    const bounds = this.elementTarget.getBoundingClientRect();
    const inset = parseFloat(getComputedStyle(header.querySelector('.site-header__container') || header).paddingLeft) || 0;
    const left = Math.max(inset, Math.min(bounds.left, document.documentElement.clientWidth - inset - bounds.width));
    this.elementTarget.style.setProperty('--dropdown-horizontal-offset', `${left - bounds.left}px`);
  }

  syncTargetState() {
    const open = this.classList.contains('is-open');
    this.elementToggle.setAttribute('aria-expanded', String(open));
    this.elementTarget.setAttribute('aria-hidden', String(!open));
    this.elementTarget.inert = !open;
  }

  addOpenListeners() {
    document.body.addEventListener('keyup', this.onKeyupEvent);
    if (this.dataset.closeOtherAnimatedDetails === 'true') {
      this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      document.body.addEventListener('click', this.onBodyClickEvent);
    }
  }

  open() {
    if (this.dataset.closeOtherAnimatedDetails === 'true') {
      document.querySelectorAll('animated-details[data-close-other-animated-details="true"]').forEach((element) => {
        element.close();
      });
    }

    this.addOpenListeners();
    this.classList.add('is-open');
    this.positionHeaderDropdown();
    this.elementTarget.setAttribute('aria-hidden', false);
    this.elementToggle.setAttribute('aria-expanded', true);
    this.syncTargetState();
  }

  close(focusToggle = false) {
    this.classList.remove('is-open');
    this.elementTarget.setAttribute('aria-hidden', true);
    this.elementToggle.setAttribute('aria-expanded', false);
    document.body.removeEventListener('keyup', this.onKeyupEvent);

    if (this.dataset.closeOtherAnimatedDetails === 'true') {
      document.body.removeEventListener('click', this.onBodyClickEvent);
    }
    if (focusToggle) this.elementToggle.focus();
    this.syncTargetState();
  }

  onBodyClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  disconnectedCallback() {
    this.stateObserver?.disconnect();
    this.close();
    this.elementTarget.style.removeProperty('--dropdown-horizontal-offset');
  }

  onKeyup(event) {
    if (event.defaultPrevented || isComposingEvent(event) || !this.classList.contains('is-open')) return;
    if (event.key !== 'Escape' && event.code?.toUpperCase() !== 'ESCAPE') return;
    if (this.dataset.closeOtherAnimatedDetails !== 'true' && event.target.closest('animated-details') !== this) return;
    const nestedDetails = event.target.closest('details[open]');
    if (nestedDetails && this.contains(nestedDetails)) {
      onKeyUpEscape(event);
      return;
    }
    event.stopPropagation();
    this.close(this.contains(event.target));
  }
}

customElements.define('animated-details', AnimatedDetails);

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

// Swiper Functions
let swiperDefinitionPending = false;

function initializeRegisteredSwipers() {
  swiperDefinitionPending = false;
  initSwipers();
  initMediaGallery();
}

function waitForSwiperDefinition(root) {
  if (customElements.get('swiper-container')) return false;
  if (root.querySelector('swiper-container') && !swiperDefinitionPending) {
    swiperDefinitionPending = true;
    // 페이지당 한 대기만 두고, 등록 시 현재 DOM을 찾는다.
    customElements.whenDefined('swiper-container').then(initializeRegisteredSwipers);
  }
  return true;
}

function initSwipers(root = document) {
  if (waitForSwiperDefinition(root)) return;
  root.querySelectorAll('swiper-container').forEach((swiperContainer) => {
    if (!isCurrentHTMLNode(swiperContainer)) return;
    // Media gallery is initialized separately in product-media-gallery.liquid
    const classesToExclude = ['js-product-media-gallery', 'js-product-media-gallery-thumbnails'];
    if (classesToExclude.some(className => swiperContainer.classList.contains(className))) return;
    if (swiperContainer.swiper?.destroyed) swiperContainer.initialized = false;
    if (!swiperContainer.swiper?.initialized || swiperContainer.swiper.destroyed) swiperContainer.initialize();
    configureSlideshow(swiperContainer);
    if (!swiperContainer.isConnected || !swiperContainer.parentElement || !swiperContainer.swiper?.initialized || swiperContainer.swiper.destroyed) return;
    swiperContainer.classList.add('loaded');
    swiperContainer.parentElement.classList.add('swiper-loaded');
  });
}

const slideshowControllers = new WeakMap();

function configureSlideshow(container) {
  const root = container.closest('.slideshow, .announcement-bar');
  const swiper = container.swiper;
  if (!root || !swiper?.initialized || swiper.destroyed) return;
  const previous = slideshowControllers.get(container);
  if (previous?.swiper === swiper) return;
  previous?.cleanup();

  const controller = new AbortController();
  const rotationControl = [...root.querySelectorAll('[data-slideshow-rotation]')].find(control => control.getAttribute('aria-controls') === container.id);
  const syncRotationControl = () => {
    const owner = slideshowControllers.get(container);
    if (!rotationControl || controller.signal.aborted || container.swiper !== swiper || (owner && owner.cleanup !== cleanup)) return;
    rotationControl.hidden = !container.isConnected || swiper.destroyed || !swiper.params.autoplay?.enabled;
    if (rotationControl.hidden) return;
    const running = Boolean(swiper.autoplay?.running);
    rotationControl.setAttribute('aria-label', running ? rotationControl.dataset.pauseLabel : rotationControl.dataset.playLabel);
    rotationControl.querySelector('[data-rotation-play]').hidden = running;
    rotationControl.querySelector('[data-rotation-pause]').hidden = !running;
  };
  const sync = () => {
    if (!container.isConnected || swiper.destroyed) return;
    const hasFocus = root.contains(document.activeElement);
    if (hasFocus) {
      // 초점이 있는 콘텐츠를 자동 전환하지 않고 감소 모션 해제 뒤에도 정지를 보존한다.
      resumeAutoplay = false;
      if (swiper.autoplay?.running) swiper.autoplay.stop();
      if (!container.isConnected || swiper.destroyed) return;
    }
    if (!swiper.keyboard || !swiper.params.keyboard?.enabled) return;
    if (root.contains(document.activeElement)) swiper.keyboard.enable();
    else swiper.keyboard.disable();
  };
  const cleanup = () => {
    if (controller.signal.aborted) return;
    controller.abort();
    const owner = slideshowControllers.get(container);
    if (rotationControl && container.swiper === swiper && (!owner || owner.cleanup === cleanup)) rotationControl.hidden = true;
    swiper.off('autoplayStart autoplayStop', syncRotationControl);
    swiper.off('destroy', cleanup);
    if (slideshowControllers.get(container)?.cleanup === cleanup) slideshowControllers.delete(container);
  };
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const configuredSpeed = swiper.params.speed;
  let reduced = false;
  let resumeAutoplay = false;
  let autoplayPaused = false;
  const syncMotion = () => {
    if (!container.isConnected || swiper.destroyed) return;
    swiper.params.speed = motionPreference.matches ? 0 : configuredSpeed;
    if (motionPreference.matches && !reduced) {
      resumeAutoplay = Boolean(swiper.autoplay?.running);
      autoplayPaused = Boolean(swiper.autoplay?.paused);
      swiper.autoplay?.stop();
      if (!container.isConnected || swiper.destroyed) return;
      swiper.setTransition(0);
      // 속도 변경만으로 남는 현재 SDK 전환을 완료하고 콘텐츠의 애니메이션은 보존한다.
      [swiper.wrapperEl, ...swiper.slides].forEach(element => {
        element.getAnimations().forEach(animation => {
          const ownedProperty = ['opacity', 'transform'].includes(animation.transitionProperty)
            || (element === swiper.wrapperEl && swiper.params.autoHeight && animation.transitionProperty === 'height');
          if (animation.effect?.target === element && !animation.effect.pseudoElement && ownedProperty) animation.finish();
        });
      });
      if (swiper.params.cssMode) swiper.wrapperEl.scrollTo({ left: swiper.wrapperEl.scrollLeft, top: swiper.wrapperEl.scrollTop, behavior: 'auto' });
    } else if (!motionPreference.matches && reduced && resumeAutoplay) {
      swiper.autoplay.start();
      if (autoplayPaused) swiper.autoplay.pause();
    }
    reduced = motionPreference.matches;
  };
  const preserveManualStop = (event) => {
    if (!reduced || !swiper.params.autoplay?.disableOnInteraction) return;
    if (event.type === 'keydown' && !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Enter', ' '].includes(event.key)) return;
    resumeAutoplay = false;
  };
  const options = { signal: controller.signal };
  let pointerWantsPlay;
  rotationControl?.addEventListener('pointerdown', () => {
    // pointer의 원래 동작 의도를 focusin에 따른 자동 정지보다 먼저 보존한다.
    pointerWantsPlay = !swiper.autoplay?.running;
  }, options);
  rotationControl?.addEventListener('pointercancel', () => { pointerWantsPlay = undefined; }, options);
  rotationControl?.addEventListener('click', (event) => {
    const wantsPlay = event.detail > 0 && pointerWantsPlay !== undefined ? pointerWantsPlay : !swiper.autoplay?.running;
    pointerWantsPlay = undefined;
    if (!container.isConnected || swiper.destroyed || container.swiper !== swiper) return;
    resumeAutoplay = false;
    if (wantsPlay) swiper.autoplay?.start();
    else if (swiper.autoplay?.running) swiper.autoplay.stop();
    syncRotationControl();
  }, options);
  swiper.on('autoplayStart autoplayStop', syncRotationControl);
  motionPreference.addEventListener('change', syncMotion, options);
  root.addEventListener('pointerdown', preserveManualStop, { ...options, capture: true });
  root.addEventListener('keydown', preserveManualStop, { ...options, capture: true });
  root.addEventListener('shopify:block:select', (event) => {
    if (reduced && event.target.matches('.block--slideshow-slide')) autoplayPaused = true;
  }, options);
  root.addEventListener('shopify:block:deselect', (event) => {
    if (reduced && event.target.matches('.block--slideshow-slide')) autoplayPaused = false;
  }, options);
  syncMotion();
  if (!container.isConnected || swiper.destroyed || container.swiper !== swiper) {
    cleanup();
    return;
  }

  root.addEventListener('focusin', sync, { signal: controller.signal });
  root.addEventListener('focusout', () => queueMicrotask(sync), { signal: controller.signal });
  swiper.on('destroy', cleanup);
  slideshowControllers.set(container, { swiper, cleanup });
  // SDK의 document 키보드 리스너는 현재 초점이 있는 배너만 소유한다.
  sync();
  syncRotationControl();
}

const swiperVideoHandlers = new WeakMap();
let mediaGalleryMotionPreference;

function getMediaGalleryMotionPreference() {
  if (!mediaGalleryMotionPreference) {
    mediaGalleryMotionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    // One page-scoped listener discovers current galleries instead of retaining removed sections.
    mediaGalleryMotionPreference.addEventListener('change', ({ matches }) => {
      document.querySelectorAll('.js-product-media-gallery, .js-product-media-gallery-thumbnails').forEach((element) => {
        const swiper = element.swiper;
        if (!swiper?.initialized || swiper.destroyed) return;
        swiper.params.speed = matches ? 0 : element.classList.contains('js-product-media-gallery') ? 500 : 300;
      });
    });
  }
  return mediaGalleryMotionPreference;
}

function handleSwiperVideos(root = document) {
  root.querySelectorAll('.js-product-media-gallery').forEach((swiperContainer) => {
    const swiper = swiperContainer.swiper;
    const previous = swiperVideoHandlers.get(swiperContainer);
    if (previous?.swiper === swiper && !swiper?.destroyed) return;
    if (previous) {
      previous.swiper.off('realIndexChange', previous.handler);
      swiperVideoHandlers.delete(swiperContainer);
    }
    if (!swiper || swiper.destroyed) return;
    const handler = () => {
      if (!swiperContainer.isConnected || swiperContainer.swiper !== swiper || swiper.destroyed) return;
      const newSlide = swiper.slides[swiper.activeIndex];
      updateMediaGalleryThumbnails(swiperContainer);
      window.pauseAllMedia();
      newSlide?.querySelector('deferred-media')?.loadContent(false);
    };
    swiper.on('realIndexChange', handler);
    swiperVideoHandlers.set(swiperContainer, { swiper, handler });
  });
}

function updateMediaGalleryThumbnails(gallery) {
  const thumbnails = document.getElementById(`swiper-thumbnails--${gallery.dataset.sectionId}`);
  const activeMedia = gallery.swiper.slides[gallery.swiper.activeIndex]?.dataset.mediaId;
  thumbnails?.querySelectorAll('button[data-media-id]').forEach((button) => {
    if (button.dataset.mediaId === activeMedia) button.setAttribute('aria-current', 'true');
    else button.removeAttribute('aria-current');
  });
}

function initMediaGallery(root = document) {
  if (waitForSwiperDefinition(root)) return;
  root.querySelectorAll('.js-product-media-gallery').forEach((gallery) => {
    if (!isCurrentHTMLNode(gallery)) return;
    if (gallery.swiper?.initialized && !gallery.swiper.destroyed) return;
    if (gallery.swiper?.destroyed) gallery.initialized = false;
    const sectionId = gallery.dataset.sectionId;
    const thumbnailsGallery = document.getElementById(`swiper-thumbnails--${sectionId}`);
    const galleryContainer = document.getElementById(gallery.dataset.galleryContainer);
    const thumbnailsGalleryPosition = gallery.dataset.thumbnailsGalleryPosition;
    const motionPreference = getMediaGalleryMotionPreference();

    // Thumbnails must initialize before the main gallery: thumbs module needs a live Swiper instance.
    let thumbnailsGallerySettings = {};
    if (thumbnailsGalleryPosition === "on-top") {
      thumbnailsGallerySettings = {
        centerInsufficientSlides: true,
        spaceBetween: 0,
        slidesPerView: 'auto',
        freeMode: true,
        resizeObserver: true,
        watchSlidesProgress: true,
        breakpoints: {
          1000: {
            centerInsufficientSlides: false,
            direction: 'vertical',
            spaceBetween: 8,
          }
        },
      }
    } else if (thumbnailsGallery) {
      thumbnailsGallerySettings = {
        centerInsufficientSlides: true,
        spaceBetween: 0,
        slidesPerView: 'auto',
        freeMode: true,
        resizeObserver: true,
        watchSlidesProgress: true
      }
    }

    if (thumbnailsGallery) {
      if (thumbnailsGallery.swiper?.destroyed) thumbnailsGallery.initialized = false;
      Object.assign(thumbnailsGallery, { ...thumbnailsGallerySettings, speed: motionPreference.matches ? 0 : 300 });
      if (!thumbnailsGallery.swiper?.initialized || thumbnailsGallery.swiper.destroyed) thumbnailsGallery.initialize();
      thumbnailsGallery.classList.add('loaded');
    }

    const slideshowSettings = {
      effect: gallery.dataset.galleryTransition,
      slidesPerView: 1,
      loop: true,
      autoHeight: true,
      keyboardControlEnabled: true,
      noSwipingClass: 'swiper-slide--no-swiping',
      spaceBetween: 0,
      speed: motionPreference.matches ? 0 : 500,
      fadeEffect: {
        crossFade: true,
      },
      navigation: {
        nextEl: `#swiper-custom-button-next--${sectionId}`,
        prevEl: `#swiper-custom-button-prev--${sectionId}`,
      },
      // Swiper writes these labels during initialization. Keep the Liquid translations.
      a11y: {
        nextSlideMessage: document.getElementById(`swiper-custom-button-next--${sectionId}`)?.getAttribute('aria-label') || '',
        prevSlideMessage: document.getElementById(`swiper-custom-button-prev--${sectionId}`)?.getAttribute('aria-label') || '',
      },
      observeParents: true,
      observeSlideChildren: true,
      resizeObserver: true,
      ...(thumbnailsGallery ? { thumbs: { swiper: thumbnailsGallery.swiper } } : {}),
    };

    Object.assign(gallery, { ...slideshowSettings });

    gallery.initialize();
    if (thumbnailsGallery) {
      thumbnailsGallery.onkeydown = (event) => {
        const button = event.target.closest('button[data-media-id]');
        if (!button || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        const slide = Array.from(gallery.swiper.slides).find((item) => item.dataset.mediaId === button.dataset.mediaId);
        if (slide) gallery.swiper.slideToLoop(Number(slide.dataset.swiperSlideIndex), gallery.swiper.params.speed, true);
      };
    }
    updateMediaGalleryThumbnails(gallery);
    gallery.classList.add('loaded');
    galleryContainer.classList.add('swiper-loaded');
  });
  handleSwiperVideos(root);
}

const productLightboxOwners = new WeakMap();
const closingProductLightboxes = new Set();
let productLightboxOpenSequence = 0;
let productLightboxLibraryLoading = null;

function getProductLightboxGalleries(root) {
  if (!root?.querySelectorAll) return [];
  const selector = '.product-media-gallery__wrapper, [data-lightbox-gallery]';
  const galleries = Array.from(root.querySelectorAll(selector));
  if (root.matches?.(selector)) galleries.unshift(root);
  return galleries;
}

function disposeProductLightboxes(root) {
  getProductLightboxGalleries(root).forEach((gallery) => {
    const name = gallery.querySelector('[data-fslightbox]')?.dataset.fslightbox;
    const registered = window.fsLightboxInstances?.[name];
    const owner = productLightboxOwners.get(gallery);
    const instance = owner?.instance || (registered?.elements.a.some((anchor) => gallery.contains(anchor)) ? registered : null);
    if (!instance) return;
    productLightboxOwners.delete(gallery);
    owner?.bindings.forEach(({ anchor, handler }) => {
      if (anchor.onclick === handler) anchor.onclick = null;
    });
    if (window.fsLightboxInstances?.[name] === instance) delete window.fsLightboxInstances[name];
    if (window.fsLightbox === instance) window.fsLightbox = Object.values(window.fsLightboxInstances || {}).at(-1);
    if (!instance.elements.container?.isConnected) return;

    // vendor의 닫기 정리가 끝난 뒤 새 확대보기를 열어 스크롤·초점 소유권을 보존한다.
    let settle;
    const closing = new Promise((resolve) => { settle = resolve; });
    closingProductLightboxes.add(closing);
    const onClose = instance.props.onClose;
    instance.props.onClose = (...args) => {
      try { onClose?.(...args); }
      finally {
        closingProductLightboxes.delete(closing);
        settle();
      }
    };
    try { instance.close(); }
    catch (error) {
      instance.props.onClose();
      console.error(error);
    }
  });
}

function loadProductLightboxLibrary(root) {
  if (productLightboxLibraryLoading) return productLightboxLibraryLoading;
  const source = root.querySelector('script[data-lightbox-src], script[src*="vendor-fslightbox.js"]');
  const sourceUrl = source?.dataset.lightboxSrc || source?.src;
  if (!sourceUrl || !sourceUrl.split(/[?#]/)[0].endsWith('/vendor-fslightbox.js')) return null;
  productLightboxLibraryLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = sourceUrl;
    script.onload = () => {
      if (typeof window.FsLightbox === 'function') resolve();
      else {
        script.remove();
        reject(new Error('Product lightbox library unavailable'));
      }
    };
    script.onerror = () => {
      script.remove();
      reject(new Error('Product lightbox library request failed'));
    };
    document.head.append(script);
  }).finally(() => { productLightboxLibraryLoading = null; });
  return productLightboxLibraryLoading;
}

function registerProductLightbox(gallery, anchors, labelsSource) {
  const name = anchors[0].dataset.fslightbox;
  let instance = window.fsLightboxInstances?.[name];
  const current = instance?.elements.a.length === anchors.length && anchors.every((anchor, index) =>
    instance.elements.a[index] === anchor && instance.props.sources[index] === anchor.getAttribute('href'));
  if (!current) {
    instance = new window.FsLightbox();
    anchors.forEach((anchor, index) => {
      instance.props.sources.push(anchor.getAttribute('href'));
      instance.elements.a.push(anchor);
      [['captions', 'data-caption'], ['types', 'data-type'], ['thumbs', 'data-thumb']].forEach(([key, attribute]) => {
        if (anchor.hasAttribute(attribute)) instance.props[key][index] = anchor.getAttribute(attribute);
      });
      const reserved = ['data-fslightbox', 'data-caption', 'data-type', 'data-thumb'];
      Array.from(anchor.attributes).forEach(({ name: attribute, value }) => {
        if (!attribute.startsWith('data-') || reserved.includes(attribute)) return;
        instance.props.customAttributes[index] ||= {};
        instance.props.customAttributes[index][attribute.slice(5)] = value;
      });
    });
    window.fsLightboxInstances ||= {};
    window.fsLightboxInstances[name] = instance;
    window.fsLightbox = instance;
  }
  const owner = { instance, labelsSource, anchors, bindings: [] };
  anchors.forEach((anchor, index) => {
    const handler = (event) => {
      event.preventDefault();
      const isCurrent = () => anchor.isConnected && productLightboxOwners.get(gallery) === owner &&
        window.fsLightboxInstances?.[name] === instance;
      if (!isCurrent()) return;
      const sequence = ++productLightboxOpenSequence;
      const open = () => {
        if (isCurrent() && productLightboxOpenSequence === sequence) instance.open(index);
      };
      if (closingProductLightboxes.size) Promise.all(Array.from(closingProductLightboxes)).then(open);
      else open();
    };
    anchor.onclick = handler;
    owner.bindings.push({ anchor, handler });
  });
  productLightboxOwners.set(gallery, owner);
  return instance;
}

function initProductLightboxes(root = document) {
  if (!root?.querySelectorAll) root = document;
  if (!root.isConnected) return;
  if (typeof window.FsLightbox !== 'function') {
    if (!root.querySelector('[data-fslightbox]')) return;
    loadProductLightboxLibrary(root)?.then(() => {
      if (root.isConnected) initProductLightboxes(root);
    }).catch((error) => console.error(error));
    return;
  }
  getProductLightboxGalleries(root).forEach((gallery) => {
    const labelScript = gallery.querySelector('[data-lightbox-labels]');
    const anchors = Array.from(gallery.querySelectorAll('a[data-fslightbox][href]'))
      .filter((anchor) => anchor.getAttribute('href'));
    if (!labelScript || !anchors.length) return;
    const owner = productLightboxOwners.get(gallery);
    const registered = window.fsLightboxInstances?.[anchors[0].dataset.fslightbox];
    if (owner && owner.instance === registered && owner.labelsSource === labelScript.textContent &&
        owner.anchors.length === anchors.length && owner.anchors.every((anchor, index) => anchor === anchors[index])) return;
    const labels = JSON.parse(labelScript.textContent);
    if (owner) disposeProductLightboxes(gallery);
    const instance = registerProductLightbox(gallery, anchors, labelScript.textContent);
    const toolbar = instance.props.toolbarButtons;
    ['thumbs', 'zoomIn', 'zoomOut', 'close'].forEach((key) => {
      toolbar[key].title = labels[key];
    });
    ['start', 'pause'].forEach((key) => { toolbar.slideshow[key].title = labels[key]; });
    ['enter', 'exit'].forEach((key) => { toolbar.fullscreen[key].title = labels[key]; });
    instance.props.slideButtons.previous.title = labels.previous;
    instance.props.slideButtons.next.title = labels.next;
    const focusOwnership = {};
    let returnFocus = null;
    let thumbsObserver = null;
    let thumbsResizeObserver = null;
    const keepFocusedThumbVisible = () => {
      const thumb = document.activeElement;
      if (!instance.elements.thumbsWrappers.includes(thumb)) return;
      const inner = instance.elements.thumbsInner;
      const viewport = instance.elements.thumbsContainer.getBoundingClientRect();
      const rect = thumb.getBoundingClientRect();
      if (!viewport.width || !rect.width) return;
      if (inner.getBoundingClientRect().width <= viewport.width) {
        instance.core.thumbsTransformer.transformToCurrent();
        return;
      }
      const translation = new DOMMatrixReadOnly(getComputedStyle(inner).transform).m41;
      const left = rect.left - translation + instance.data.thumbsTransform;
      const right = left + rect.width;
      const delta = left < viewport.left ? viewport.left - left
        : right > viewport.right ? viewport.right - right : 0;
      if (!delta) return;
      instance.data.thumbsTransform += delta;
      inner.style.transform = `translateX(${instance.data.thumbsTransform}px)`;
    };
    const updateThumbs = () => {
      const thumbsVisible = instance.data.isThumbing
        && !instance.elements.thumbsContainer.classList.contains('fslightboxx');
      instance.elements.thumbsWrappers.forEach((thumb, index) => {
        thumb.setAttribute('role', 'button');
        thumb.tabIndex = thumbsVisible ? 0 : -1;
        thumb.setAttribute('aria-label', labels.image.replace('__index__', index + 1));
        if (index === instance.stageIndexes.current) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (thumbsVisible) keepFocusedThumbVisible();
      else if (instance.elements.thumbsWrappers.includes(document.activeElement)) {
        const toggle = Array.from(instance.elements.container.querySelectorAll('.fslightbox-toolbar-button'))
          .find((control) => control.title === labels.thumbs);
        const rect = toggle?.getBoundingClientRect();
        const target = rect?.width > 0 && rect.height > 0
          && !toggle.closest('.fslightboxx, .fslightbox-opacity-0')
          ? toggle : instance.elements.container;
        target.focus({ preventScroll: true });
      }
    };
    instance.props.onSlideChange = updateThumbs;

    instance.props.onOpen = () => {
      returnFocus = document.activeElement;
      const container = instance.elements.container;
      container.setAttribute('role', 'dialog');
      container.setAttribute('aria-modal', 'true');
      container.setAttribute('aria-label', labels.dialog);
      container.tabIndex = -1;
      const controls = [
        ...container.querySelectorAll('.fslightbox-toolbar-button'),
        instance.elements.slideButtonPrevious,
        instance.elements.slideButtonNext,
      ].filter(Boolean);
      controls.forEach((control) => {
        control.setAttribute('role', 'button');
        control.tabIndex = 0;
      });
      updateThumbs();
      // The vendor creates thumbnails asynchronously on their first toggle.
      thumbsObserver = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.type === 'childList'
          || mutation.target === instance.elements.thumbsContainer)) updateThumbs();
      });
      thumbsObserver.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      container.addEventListener('focusin', keepFocusedThumbVisible);
      thumbsResizeObserver = new ResizeObserver(keepFocusedThumbVisible);
      thumbsResizeObserver.observe(instance.elements.thumbsInner);
      thumbsResizeObserver.observe(instance.elements.thumbsContainer);
      container.onkeydown = (event) => {
        const control = event.target.closest('[role="button"]');
        if (!control || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        event.stopPropagation();
        const index = instance.elements.thumbsWrappers.indexOf(control);
        if (index >= 0) instance.core.slideIndexChanger.jumpTo(index);
        else control.click();
      };
      const close = controls.find((control) => control.title === labels.close);
      trapFocus(container, close || container, focusOwnership);
    };

    instance.props.onClose = () => {
      thumbsResizeObserver?.disconnect();
      thumbsResizeObserver = null;
      instance.elements.container.removeEventListener('focusin', keepFocusedThumbVisible);
      thumbsObserver?.disconnect();
      thumbsObserver = null;
      instance.elements.container.onkeydown = null;
      const opener = returnFocus;
      const handler = focusOwnership.focusTrapHandler;
      focusOwnership.focusTrapHandler = null;
      returnFocus = null;
      if (handler && trapFocusHandlers.keydown === handler && trapFocusHandlers.container === instance.elements.container) {
        removeTrapFocus(opener?.isConnected ? opener : null);
      }
    };
  });
}

window.addEventListener("load", fadeInImages);
window.addEventListener("load", loadAnimations);
window.addEventListener("load", setCssVariables);
window.addEventListener("load", () => initSwipers());
window.addEventListener("load", () => initMediaGallery());
window.addEventListener("load", () => handleSwiperVideos());
window.addEventListener("load", initProductLightboxes);
window.addEventListener('pagehide', () => disposeProductLightboxes(document));
window.addEventListener('pageshow', () => initProductLightboxes(document));
window.addEventListener("load", watchForResize);
